/**
 * <StudentGameSummary> — le jeu de l'élève, résumé pour son accueil (organism).
 *
 * Le livre termine l'accueil de l'élève par une carte de défi et une ligne de ligue (chap. 17).
 * L'app n'a ni l'un ni l'autre : à leur place, ce qu'elle a. La quête où l'élève en est, dans sa
 * carte (`<QuestCard>`), puis son rang au classement général, en une ligne. Chacune ouvre son
 * écran, comme les lignes « Quêtes » et « Classements » de « Mon compte ».
 *
 * Ce qui manque ne prend pas de place : sans quête, pas de carte ; sans rang (aucun XP encore),
 * pas de ligne ; sans rien, le bloc ne se rend pas, et retirer le jeu, c'est ne plus le monter.
 *
 * Deux places gardées, vides aujourd'hui : `challenge` sous la quête (le défi de la semaine du
 * livre, une carte de la même forme) et `league` sous le rang (sa ligne de ligue).
 */
import React from "react"
import { Pressable, View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Icon } from "../../atoms/icons/Icon"
import { NotificationDot } from "../../atoms/NotificationDot"
import { Text } from "../../atoms/typography/Text"
import { QuestCard, type UIQuest } from "../QuestCard"

export type StudentGameSummaryProps = {
    /** La quête où l'élève en est : la plus avancée, celle que « Mon compte » montre. */
    quest?: UIQuest | null
    /** Celui qui porte la quête, pour la fente de sa carte : `<QuestChampion>`. */
    champion?: React.ReactNode
    /** Ouvre « Mes quêtes ». */
    onOpenQuests?: () => void
    /** Son rang au classement général, « 12ᵉ ». Absent : il n'est pas encore classé. */
    rankLabel?: string | null
    /** Ouvre le classement. */
    onOpenRanking?: () => void
    /** Sous la quête. Rien aujourd'hui. */
    challenge?: React.ReactNode
    /** Sous le rang. Rien aujourd'hui. */
    league?: React.ReactNode
    testID?: string
}

const TEXTS = {
    ranking: "Classement général",
}

/** La hauteur d'une ligne qui presse. */
export const SUMMARY_LINE_HEIGHT = 44

export function StudentGameSummary(props: StudentGameSummaryProps) {
    const { quest, champion, onOpenQuests, rankLabel, onOpenRanking, challenge, league, testID } = props

    const hasRank = rankLabel != null && rankLabel !== ""
    if (quest == null && !hasRank && challenge == null && league == null) return null

    return (
        <Stack testID={testID} gap="2">
            {quest != null && <QuestCard quest={quest} champion={champion} onPress={onOpenQuests} />}
            {challenge}
            {hasRank && <RankLine label={`${TEXTS.ranking} · ${rankLabel}`} onPress={onOpenRanking} />}
            {league}
        </Stack>
    )
}

type RankLineProps = { label: string; onPress?: () => void }

/** La ligne du livre sous l'accueil : un point à la couleur de l'univers, puis une phrase. Pas une carte. */
function RankLine(props: RankLineProps) {
    const { label, onPress } = props

    const content = (
        <Row gap="2" style={styles.line}>
            <NotificationDot color="role" size={8} ringColor="role" />
            <Text size="text-md" weight="bold" numberOfLines={1} style={styles.label}>
                {label}
            </Text>
            {onPress != null && <Icon name="chevron-right" size={16} color="text-muted" />}
        </Row>
    )

    if (onPress == null) return <View>{content}</View>

    return (
        <Pressable onPress={onPress} accessibilityRole="button" accessibilityLabel={label}>
            {content}
        </Pressable>
    )
}

const styles = StyleSheet.create((theme) => ({
    /** Alignée sur le bord du texte de la carte au-dessus. */
    line: { minHeight: SUMMARY_LINE_HEIGHT, paddingHorizontal: theme.spacing["4"] },
    label: { flexShrink: 1 },
}))
